// Auto-crop for front/back card photos: finds a card lying on a surface
// (desk, mat, bedsheet), straightens up to ±10° of tilt and crops to it with
// a thin margin so the card's own edges are never shaved (graders look at
// them). Runs on-device in a few tens of ms; no network, no dependencies.
//
// Confidence-gated: whenever the photo doesn't clearly show one card outline
// on a calm surface — card already fills the frame or the photo is already
// cropped, patterned surface, outline too faint — it returns the photo
// untouched. Callers offer an undo, so a bad crop is one tap away from the
// original.

const WORK = 256; // detection runs on a copy this size (longest side)

// Box-blur an RGBA buffer in place (3x3, alpha ignored). Done by hand rather
// than ctx.filter, which iOS Safari only supports from 18.
function blur3(px, W, H) {
  const src = px.slice();
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      let r = 0, g = 0, b = 0, n = 0;
      for (let dy = -1; dy <= 1; dy++) {
        const yy = y + dy;
        if (yy < 0 || yy >= H) continue;
        for (let dx = -1; dx <= 1; dx++) {
          const xx = x + dx;
          if (xx < 0 || xx >= W) continue;
          const i = (yy * W + xx) * 4;
          r += src[i]; g += src[i + 1]; b += src[i + 2]; n++;
        }
      }
      const o = (y * W + x) * 4;
      px[o] = r / n; px[o + 1] = g / n; px[o + 2] = b / n;
    }
  }
}

// Find the card. Returns { cx, cy, w, h, angle, bg } in source pixels (angle
// in degrees, the card's tilt) or { skip: reason } when not confident.
//
// Works from EDGES, not from "differs from the surface colour": a card's
// outline is a sharp straight rectangle, while uneven lighting, a dark mat's
// gradient and the phone's own shadow change slowly — after the blur they
// barely register as edges, so they can't drag the box around.
export function detectCard(src) {
  const sw = src.width, sh = src.height;
  if (!sw || !sh) return { skip: 'empty image' };
  // A photo that is itself card-shaped has already been cropped (a scan, an
  // edit in Photos, or our own output). Phone cameras shoot 3:4, 1:1 or 16:9,
  // never 5:7 — and inside a cropped card the only "outline" left to find is
  // the card's printed frame, which would shave off its border.
  const photoAr = Math.min(sw, sh) / Math.max(sw, sh);
  if (photoAr > 0.69 && photoAr < 0.735) return { skip: 'photo is already card-shaped' };
  const k = Math.min(1, WORK / Math.max(sw, sh));
  const W = Math.max(1, Math.round(sw * k)), H = Math.max(1, Math.round(sh * k));
  if (W < 32 || H < 32) return { skip: 'image too small' };
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const ctx = c.getContext('2d', { willReadFrequently: true });
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(src, 0, 0, W, H);
  const px = ctx.getImageData(0, 0, W, H).data;
  blur3(px, W, H); blur3(px, W, H); // melt mat texture / sleeve glare speckle

  // Surface colour (only used to fill corners uncovered by straightening).
  const ring = Math.max(2, Math.round(Math.min(W, H) * 0.04));
  const rs = [], gs = [], bs = [];
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      if (x >= ring && x < W - ring && y >= ring && y < H - ring) continue;
      const i = (y * W + x) * 4;
      rs.push(px[i]); gs.push(px[i + 1]); bs.push(px[i + 2]);
    }
  }
  const med = (a) => a.slice().sort((p, q) => p - q)[a.length >> 1];
  const bg = [med(rs), med(gs), med(bs)];

  // 1. Edges: Sobel per colour channel, keeping the strongest channel so a
  // red card on a grey mat of equal brightness still has an outline.
  const gx = new Float32Array(W * H), gy = new Float32Array(W * H), G = new Float32Array(W * H);
  for (let y = 1; y < H - 1; y++) {
    for (let x = 1; x < W - 1; x++) {
      let bx = 0, by = 0, bm = -1;
      for (let ch = 0; ch < 3; ch++) {
        const p = (yy, xx) => px[(yy * W + xx) * 4 + ch];
        const ex = (p(y - 1, x + 1) + 2 * p(y, x + 1) + p(y + 1, x + 1)) - (p(y - 1, x - 1) + 2 * p(y, x - 1) + p(y + 1, x - 1));
        const ey = (p(y + 1, x - 1) + 2 * p(y + 1, x) + p(y + 1, x + 1)) - (p(y - 1, x - 1) + 2 * p(y - 1, x) + p(y - 1, x + 1));
        const m = ex * ex + ey * ey;
        if (m > bm) { bm = m; bx = ex; by = ey; }
      }
      const j = y * W + x;
      gx[j] = bx; gy[j] = by; G[j] = Math.sqrt(bm);
    }
  }
  const sorted = Array.from(G).sort((p, q) => p - q);
  const gTop = sorted[Math.floor(sorted.length * 0.98)];
  if (gTop < 20) return { skip: 'no clear edges' };
  const tau = gTop * 0.25; // "this pixel is on an edge"
  const ex = [], ey = [], ecx = [], ecy = [];
  for (let y = 1; y < H - 1; y++) {
    for (let x = 1; x < W - 1; x++) {
      const j = y * W + x;
      if (G[j] > tau) { ex.push(x); ey.push(y); ecx.push(gx[j]); ecy.push(gy[j]); }
    }
  }

  // 2. Tilt = the angle (±10°) where edge strength piles up into the fewest
  // columns and rows — the card's four sides lining up with the axes.
  const mx = W / 2, my = H / 2;
  const D = Math.ceil(Math.hypot(W, H)) + 4, half = D / 2;
  const profiles = (a) => {
    const t = (a * Math.PI) / 180, co = Math.cos(t), si = Math.sin(t);
    const col = new Float32Array(D), row = new Float32Array(D);
    for (let j = 0; j < ex.length; j++) {
      const dx = ex[j] - mx, dy = ey[j] - my;
      const u = Math.round(dx * co + dy * si + half), v = Math.round(-dx * si + dy * co + half);
      col[u] += Math.abs(ecx[j] * co + ecy[j] * si); // gradient across a vertical side
      row[v] += Math.abs(-ecx[j] * si + ecy[j] * co); // across a horizontal side
    }
    let score = 0;
    for (let q = 0; q < D; q++) score += col[q] * col[q] + row[q] * row[q];
    return { a, score, col, row };
  };
  let best = null;
  for (let a = -10; a <= 10.001; a += 0.5) {
    const p = profiles(a);
    if (!best || p.score > best.score) best = p;
  }
  for (const a of [best.a - 0.25, best.a + 0.25]) {
    const p = profiles(a);
    if (p.score > best.score) best = p;
  }
  const t = (best.a * Math.PI) / 180, co = Math.cos(t), si = Math.sin(t);

  // 3. Edge maps in the card's (u, v) frame, to score candidate rectangles.
  const Ev = new Uint8Array(D * D), Eh = new Uint8Array(D * D);
  for (let j = 0; j < ex.length; j++) {
    const dx = ex[j] - mx, dy = ey[j] - my;
    const u = Math.round(dx * co + dy * si + half), v = Math.round(-dx * si + dy * co + half);
    const nu = Math.abs(ecx[j] * co + ecy[j] * si), nv = Math.abs(-ecx[j] * si + ecy[j] * co);
    if (nu > nv) Ev[v * D + u] = 1; else Eh[v * D + u] = 1;
  }
  const inImage = (u, v) => {
    const x = mx + (u - half) * co - (v - half) * si, y = my + (u - half) * si + (v - half) * co;
    return x >= 1 && x < W - 1 && y >= 1 && y < H - 1;
  };

  // Candidate sides = the strongest peaks of each profile.
  const peaks = (p) => {
    const s = new Float32Array(D);
    for (let q = 1; q < D - 1; q++) s[q] = p[q - 1] + 2 * p[q] + p[q + 1];
    let top = 0;
    for (let q = 0; q < D; q++) if (s[q] > top) top = s[q];
    const out = [];
    for (let q = 2; q < D - 2; q++) {
      if (s[q] >= top * 0.08 && s[q] >= s[q - 1] && s[q] > s[q + 1] && s[q] >= s[q - 2] && s[q] > s[q + 2]) out.push([q, s[q]]);
    }
    return out.sort((p1, p2) => p2[1] - p1[1]).slice(0, 16).map((e) => e[0]);
  };
  const us = peaks(best.col), vs = peaks(best.row);

  // 4. Score every card-shaped rectangle by how much of its outline is
  // actually edge (±1px). Sides running off the photo don't count either way,
  // but a card mostly out of frame is rejected.
  const side = (fixed, from, to, vertical) => {
    let hit = 0, seen = 0;
    for (let q = from; q <= to; q++) {
      const u = vertical ? fixed : q, v = vertical ? q : fixed;
      if (!inImage(u, v)) continue;
      seen++;
      for (let o = -1; o <= 1; o++) {
        const i = vertical ? v * D + u + o : (v + o) * D + u;
        if ((vertical ? Ev : Eh)[i]) { hit++; break; }
      }
    }
    return [hit, seen];
  };
  let cands = [];
  for (let i = 0; i < us.length; i++) {
    for (let j = 0; j < us.length; j++) {
      const u0 = us[i], u1 = us[j];
      if (u1 - u0 < 12) continue;
      for (let a = 0; a < vs.length; a++) {
        for (let b = 0; b < vs.length; b++) {
          const v0 = vs[a], v1 = vs[b];
          if (v1 - v0 < 12) continue;
          const w = u1 - u0, h = v1 - v0;
          const ar = Math.min(w, h) / Math.max(w, h);
          if (ar < 0.66 || ar > 0.78) continue;
          const frac = (w * h) / (W * H);
          if (frac < 0.06 || frac > 0.8) continue;
          let hit = 0, seen = 0;
          for (const [hh, ss] of [side(u0, v0, v1, true), side(u1, v0, v1, true), side(v0, u0, u1, false), side(v1, u0, u1, false)]) { hit += hh; seen += ss; }
          if (seen < 2 * (w + h) * 0.7) continue;
          cands.push({ u0, u1, v0, v1, w, h, ar, frac, cover: hit / seen });
        }
      }
    }
  }
  if (!cands.length) return { skip: 'no card outline', angle: best.a };
  const top = cands.reduce((m, q) => Math.max(m, q.cover), 0);
  if (top < 0.55) return { skip: 'outline too weak', angle: best.a, cover: top };
  // The card's own inner frame lines can form a neat rectangle too — among
  // near-equally clean outlines take the biggest, which is the card's edge.
  cands = cands.filter((q) => q.cover >= top - 0.12).sort((p1, p2) => p2.w * p2.h - p1.w * p1.h);
  const r = { ...cands[0] };

  // 4b. Grow each side out over any card left outside it. Where dark card
  // art meets a dark mat the real edge is faint, and a bright line just
  // inside the card (a hat brim, a logo box) can win instead — clipping the
  // corner. So each side walks outward (at most 12% of the card) while it
  // still finds pixels that can only be card, and stops at open mat. The
  // phone's shadow beside a card is "not mat" too, but it only ever darkens,
  // so it never pulls a side out.
  const colorAt = (u, v) => {
    const x = Math.round(mx + (u - half) * co - (v - half) * si), y = Math.round(my + (u - half) * si + (v - half) * co);
    if (x < 0 || y < 0 || x >= W || y >= H) return null;
    const i = (y * W + x) * 4;
    return [px[i], px[i + 1], px[i + 2]];
  };
  // Colours along a line parallel to a side (its length, 3px clear of the
  // corners), position-aligned; null where the line leaves the photo.
  const strip = (vertical, p, a, b) => {
    const out = [];
    for (let q = a + 3; q <= b - 3; q += Math.max(1, (b - a) / 64)) {
      out.push(vertical ? colorAt(p, q) : colorAt(q, p));
    }
    return out;
  };
  const dist3 = (p, q) => Math.hypot(p[0] - q[0], p[1] - q[1], p[2] - q[2]);
  // How many px of photo lie beyond a side.
  const room = (key, dir, vertical) => {
    const a = vertical ? r.v0 : r.u0, b = vertical ? r.v1 : r.u1;
    let o = 0;
    while (o < D && strip(vertical, r[key] + dir * (o + 1), a, b).some(Boolean)) o++;
    return o;
  };
  // Shadows only darken the mat; they never brighten it or change its hue.
  // So "definitely card" = brighter than the mat at the same spot, or a
  // clearly different colour (a logo, a light border).
  const lum = (p) => 0.299 * p[0] + 0.587 * p[1] + 0.114 * p[2];
  const hue = (p, q) => {
    const sp = p[0] + p[1] + p[2] + 1, sq = q[0] + q[1] + q[2] + 1;
    return Math.hypot(p[0] / sp - q[0] / sq, p[1] / sp - q[1] / sq, p[2] / sp - q[2] / sq);
  };
  const grow = (key, dir, vertical) => {
    const space = room(key, dir, vertical);
    const reach = Math.min(space - 6, Math.max(3, Math.round((vertical ? r.w : r.h) * 0.12)));
    if (reach < 3) return; // card right at the photo's edge: nothing to compare, keep it
    const a = vertical ? r.v0 : r.u0, b = vertical ? r.v1 : r.u1;
    // Mat references from just past the reach out toward the photo's edge,
    // compared point by point along the side. "Brighter than the mat" means
    // brighter than the brightest of them, so a mat that darkens toward the
    // photo's edge (vignetting, the phone's shadow) never reads as card.
    const far = Math.min(space - 2, Math.max(reach + 4, Math.round(space * 0.7)));
    const refs = [reach + 2, reach + 4, far - 1, far + 1].map((o) => strip(vertical, r[key] + dir * o, a, b));
    const noise = [];
    refs[2].forEach((p, i) => { if (p && refs[3][i]) noise.push(dist3(p, refs[3][i])); });
    if (noise.length < 8) return;
    const tol = Math.max(25, 3 * med(noise));
    let last = 0;
    for (let o = 1; o <= reach; o++) {
      const s = strip(vertical, r[key] + dir * o, a, b);
      let card = 0, n = 0;
      s.forEach((p, i) => {
        const qs = refs.map((rr) => rr[i]).filter(Boolean);
        if (!p || !qs.length) return;
        n++;
        // Shadows scale brightness by a factor, so "brighter" has to be by a
        // factor too: lit mat beside its own shadow is ~1.5-2x, a light
        // border or a white logo on a dark mat is 3x and up.
        const brightest = Math.max(...qs.map(lum));
        if (lum(p) > Math.max(brightest * 2.2, brightest + tol) || (lum(p) > 50 && qs.every((q) => hue(p, q) > 0.06))) card++;
      });
      // Card must continue unbroken from the side outward (a logo in one
      // corner is enough); the first strip without any ends the walk.
      if (n < 8 || card < Math.max(3, n * 0.04)) break;
      last = o;
    }
    // 1-2px is just the blur of the edge we're already on. Beyond that, card
    // was sticking out here, and its last stretch before the real edge may
    // be plain dark art: give that side a little extra room.
    if (last >= 3) r[key] += dir * Math.min(last + 2, reach);
  };


  // Twice, so a side re-checks along its neighbours' grown length (a corner
  // logo sits beyond both the top and the left side).
  for (let pass = 0; pass < 2; pass++) {
    grow('u0', -1, true); grow('u1', 1, true); grow('v0', -1, false); grow('v1', 1, false);
  }

  // 4c. Dark printed border on a dark mat. Only the border's inner edge
  // (black to bright art) shows strongly, so the box can sit on it — and in
  // a photo taken slightly off-overhead (a keystone), on the inner edge for
  // some sides and the outer edge for others, or drifting along one side. At
  // each point along a side, find the run of "much darker than the mat"
  // nearest the side. A printed border gives most points on each side such
  // a run, all about the same width; then each side moves out to cover the
  // runs' outer ends. Shadows don't frame a card evenly on three sides.
  const bands = [['u0', -1, true], ['u1', 1, true], ['v0', -1, false], ['v1', 1, false]].map(([key, dir, vertical]) => {
    const space = room(key, dir, vertical);
    const dim = vertical ? r.w : r.h;
    const reach = Math.min(space - 6, Math.round(dim * 0.12)), back = Math.round(dim * 0.12);
    if (reach < 3) return null;
    const a = vertical ? r.v0 : r.u0, b = vertical ? r.v1 : r.u1;
    const far = Math.min(space - 2, Math.max(reach + 4, Math.round(space * 0.7)));
    const ref = strip(vertical, r[key] + dir * far, a, b);
    const rows = [];
    for (let o = -back; o <= reach; o++) rows.push(strip(vertical, r[key] + dir * o, a, b));
    const his = [], widths = [];
    let pts = 0;
    ref.forEach((q, i) => {
      if (!q || lum(q) < 25) return; // a black mat hides a black border anyway
      pts++;
      const isDark = (k) => { const p = rows[k][i]; return !!p && lum(p) < lum(q) * 0.65; };
      // The dark run nearest the side (offset 0 = row `back`).
      let best2 = null;
      for (let k = 0; k < rows.length; k++) {
        if (!isDark(k)) continue;
        let e = k;
        while (e + 1 < rows.length && isDark(e + 1)) e++;
        const dist = Math.min(Math.abs(k - back), Math.abs(e - back), k <= back && back <= e ? 0 : Infinity);
        if (!best2 || dist < best2.dist) best2 = { lo: k - back, hi: e - back, dist };
        k = e;
      }
      if (best2 && best2.dist <= Math.max(4, dim * 0.1) && best2.hi - best2.lo >= 1) {
        his.push(best2.hi); widths.push(best2.hi - best2.lo + 1);
      }
    });
    if (pts < 8 || his.length < pts * 0.6) return { key, dir, width: 0 };
    his.sort((p, q) => p - q);
    // Printed borders are thin (~3-6% of the card); a wider dark run is dark
    // art or a shadow, not a border.
    const width = med(widths);
    if (width > dim * 0.08) return { key, dir, width: 0 };
    return { key, dir, width, hi: his[Math.floor(his.length * 0.9)] };
  });
  // At least three sides must agree; a fourth that showed nothing (glare,
  // the mat lit just as dark) moves out by the same width — at worst a
  // sliver of mat, never a cut card.
  const found = bands.filter((s) => s && s.width >= 2);
  if (found.length >= 3) {
    const ws = found.map((s) => s.width);
    if (Math.max(...ws) <= 2.5 * Math.min(...ws)) {
      const wMed = med(ws);
      for (const s of bands) {
        if (!s) continue;
        if (s.width >= 2) { if (s.hi > 0) r[s.key] += s.dir * (s.hi + 1); } else r[s.key] += s.dir * wMed;
      }
    }
  }
  r.w = r.u1 - r.u0; r.h = r.v1 - r.v0;
  r.ar = Math.min(r.w, r.h) / Math.max(r.w, r.h);
  r.frac = (r.w * r.h) / (W * H);
  if (r.ar < 0.62 || r.ar > 0.82 || r.frac > 0.85) return { skip: 'not card-shaped after growing', angle: best.a, cover: r.cover };

  // 5. Just outside the outline must be calm surface. A busy band means a
  // patterned surface or a frame line inside a card — leave it alone.
  const gap = 2, band = Math.max(3, Math.round(Math.min(r.w, r.h) * 0.05));
  let bandPx = 0, bandHits = 0, outPx = 0, outHits = 0;
  for (let y = 1; y < H - 1; y++) {
    for (let x = 1; x < W - 1; x++) {
      const dx = x - mx, dy = y - my;
      const u = dx * co + dy * si + half, v = -dx * si + dy * co + half;
      const outU = u < r.u0 - gap || u > r.u1 + gap, outV = v < r.v0 - gap || v > r.v1 + gap;
      if (!outU && !outV) continue;
      const strong = G[y * W + x] > tau * 2; // strong edges only: fine mat texture is fine
      outPx++; if (strong) outHits++;
      if (u < r.u0 - gap - band || u > r.u1 + gap + band || v < r.v0 - gap - band || v > r.v1 + gap + band) continue;
      bandPx++;
      if (strong) bandHits++;
    }
  }
  // The whole area out to the photo's edge must be calm too: when the card
  // fills or overflows the frame its real edge is missing, and the best
  // "outline" left is a printed frame line with card art all around it.
  // (Measured: ≤1.2% strong-edge pixels around real cards vs ≥8.7% around
  // inner frames.)
  const busy = bandPx ? bandHits / bandPx : 0;
  const outside = outPx ? outHits / outPx : 0;
  if (busy > 0.15 || outside > 0.04) return { skip: 'busy surroundings', angle: best.a, cover: r.cover, busy, outside, frac: r.frac };

  const uc = (r.u0 + r.u1) / 2 - half, vc = (r.v0 + r.v1) / 2 - half;
  const cx = mx + uc * co - vc * si, cy = my + uc * si + vc * co;
  // 6. Already-cropped photo? Then the "outline" we found is the card's own
  // printed frame: dead straight and inset evenly on all four sides. A card
  // shot by hand is essentially never both, so leave those alone.
  let x0 = W, x1 = 0, y0 = H, y1 = 0;
  for (const [u, v] of [[r.u0, r.v0], [r.u1, r.v0], [r.u0, r.v1], [r.u1, r.v1]]) {
    const x = mx + (u - half) * co - (v - half) * si, y = my + (u - half) * si + (v - half) * co;
    x0 = Math.min(x0, x); x1 = Math.max(x1, x); y0 = Math.min(y0, y); y1 = Math.max(y1, y);
  }
  const margins = [x0 / W, (W - x1) / W, y0 / H, (H - y1) / H];
  if (Math.abs(best.a) <= 0.5 && Math.max(...margins) < 0.14) {
    return { skip: 'looks already cropped', angle: best.a, cover: r.cover, frac: r.frac, margins };
  }

  const pad = 1.03; // never shave the card's own edges
  return {
    cx: cx / k, cy: cy / k, w: (r.w * pad) / k, h: (r.h * pad) / k, angle: best.a, bg,
    ar: r.ar, frac: r.frac, cover: r.cover, busy,
  };
}

// Crop a decoded image (ImageBitmap / <img> / canvas) to the card. Returns
// { src, cropped }: a straightened canvas no larger than maxDim on its long
// side, or the untouched source when detection isn't confident or anything
// throws. maxDim keeps the canvas under iOS's canvas-size ceiling even for
// 50–108MP Android camera files.
export function cropToCard(src, maxDim = 2400) {
  try {
    const r = detectCard(src);
    if (r.skip) return { src, cropped: false };
    const s = Math.min(1, maxDim / Math.max(r.w, r.h));
    const out = document.createElement('canvas');
    out.width = Math.round(r.w * s);
    out.height = Math.round(r.h * s);
    const ctx = out.getContext('2d');
    ctx.imageSmoothingQuality = 'high';
    ctx.fillStyle = `rgb(${r.bg.join(',')})`; // corners uncovered by straightening
    ctx.fillRect(0, 0, out.width, out.height);
    ctx.translate(out.width / 2, out.height / 2);
    ctx.scale(s, s);
    ctx.rotate((-r.angle * Math.PI) / 180);
    ctx.drawImage(src, -r.cx, -r.cy);
    return { src: out, cropped: true };
  } catch (_e) {
    return { src, cropped: false };
  }
}
